import { useState, useCallback, useRef, useEffect } from "react";
import { THEMES, Theme, Difficulty } from "./themes";
import { generatePuzzle, GeneratedPuzzle, PlacedWord } from "./generator";
import { usePlayer } from "../../contexts/PlayerContext";

interface Selection {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

function getCellsInSelection(selection: Selection): Array<{ row: number; col: number }> {
  const { startRow, startCol, endRow, endCol } = selection;
  const cells: Array<{ row: number; col: number }> = [];

  const dRow = endRow === startRow ? 0 : endRow > startRow ? 1 : -1;
  const dCol = endCol === startCol ? 0 : endCol > startCol ? 1 : -1;

  // Only allow straight lines (horizontal, vertical, or diagonal)
  const rowDist = Math.abs(endRow - startRow);
  const colDist = Math.abs(endCol - startCol);

  if (rowDist !== 0 && colDist !== 0 && rowDist !== colDist) {
    // Not a valid diagonal
    return [{ row: startRow, col: startCol }];
  }

  const steps = Math.max(rowDist, colDist);

  for (let i = 0; i <= steps; i++) {
    cells.push({
      row: startRow + i * dRow,
      col: startCol + i * dCol,
    });
  }

  return cells;
}

function wordMatchesCells(word: PlacedWord, cells: Array<{ row: number; col: number }>): boolean {
  if (word.cells.length !== cells.length) return false;

  // Check forward match
  const forwardMatch = word.cells.every(
    (c, i) => c.row === cells[i].row && c.col === cells[i].col
  );
  if (forwardMatch) return true;

  // Check reverse match
  const reversedCells = [...cells].reverse();
  const reverseMatch = word.cells.every(
    (c, i) => c.row === reversedCells[i].row && c.col === reversedCells[i].col
  );

  return reverseMatch;
}

export function WordSearch() {
  const { currentPlayer, updatePlayerProgress } = usePlayer();
  const [theme, setTheme] = useState<Theme>(THEMES[0]);
  const [difficulty, setDifficulty] = useState<Difficulty>("easy");
  const [puzzle, setPuzzle] = useState<GeneratedPuzzle | null>(null);
  const [foundWords, setFoundWords] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<Selection | null>(null);
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set());
  const [startTime, setStartTime] = useState<number>(0);
  const gridRef = useRef<HTMLDivElement>(null);

  // Calculate cell size (needed in callbacks)
  const cellSize = puzzle ? Math.min(36, Math.floor((window.innerWidth - 64) / puzzle.size)) : 36;

  const startNewGame = useCallback(() => {
    const newPuzzle = generatePuzzle(theme.words, difficulty);
    setPuzzle(newPuzzle);
    setFoundWords(new Set());
    setFoundCells(new Set());
    setSelection(null);
    setStartTime(Date.now());
  }, [theme, difficulty]);

  useEffect(() => {
    startNewGame();
  }, [startNewGame]);

  const getCellFromPoint = useCallback((clientX: number, clientY: number): { row: number; col: number } | null => {
    if (!gridRef.current || !puzzle) return null;

    const gridRect = gridRef.current.getBoundingClientRect();
    const x = clientX - gridRect.left;
    const y = clientY - gridRect.top;

    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);

    if (row >= 0 && row < puzzle.size && col >= 0 && col < puzzle.size) {
      return { row, col };
    }

    return null;
  }, [puzzle, cellSize]);

  const handlePointerDown = (row: number, col: number) => {
    setSelection({ startRow: row, startCol: col, endRow: row, endCol: col });
  };

  const handleGridPointerMove = useCallback((e: React.PointerEvent) => {
    if (!selection) return;

    const cell = getCellFromPoint(e.clientX, e.clientY);
    if (cell) {
      setSelection({ ...selection, endRow: cell.row, endCol: cell.col });
    }
  }, [selection, getCellFromPoint]);

  const handlePointerUp = () => {
    if (!selection || !puzzle) {
      setSelection(null);
      return;
    }

    const cells = getCellsInSelection(selection);

    // Check if selection matches any unfound word
    for (const placedWord of puzzle.placedWords) {
      if (!foundWords.has(placedWord.word) && wordMatchesCells(placedWord, cells)) {
        const newFoundWords = new Set(foundWords);
        newFoundWords.add(placedWord.word);
        setFoundWords(newFoundWords);

        const newFoundCells = new Set(foundCells);
        placedWord.cells.forEach((c) => newFoundCells.add(`${c.row}-${c.col}`));
        setFoundCells(newFoundCells);

        // Check for win
        if (newFoundWords.size === puzzle.placedWords.length) {
          const timeMs = Date.now() - startTime;
          const puzzleId = `${theme.name}-${difficulty}`;

          if (currentPlayer) {
            updatePlayerProgress((p) => {
              const newCompleted = p.wordSearch.completed.includes(puzzleId)
                ? p.wordSearch.completed
                : [...p.wordSearch.completed, puzzleId];

              const currentBest = p.wordSearch.bestTimes[puzzleId] ?? Infinity;
              const newBestTimes = {
                ...p.wordSearch.bestTimes,
                [puzzleId]: Math.min(currentBest, timeMs),
              };

              return {
                ...p,
                wordSearch: { completed: newCompleted, bestTimes: newBestTimes },
              };
            });
          }
        }
        break;
      }
    }

    setSelection(null);
  };

  const isWon = puzzle && foundWords.size === puzzle.placedWords.length;
  const elapsedTime = isWon ? Math.floor((Date.now() - startTime) / 1000) : 0;

  if (!puzzle) return null;

  const selectionCells = selection ? getCellsInSelection(selection) : [];
  const selectionSet = new Set(selectionCells.map((c) => `${c.row}-${c.col}`));

  return (
    <div>
      <div className="game-header">
        <span>{theme.name}</span>
        <span>
          {foundWords.size}/{puzzle.placedWords.length}
        </span>
      </div>

      {isWon && (
        <div className="success-message">
          <h2>🎉 All Words Found!</h2>
          <p>Completed in {elapsedTime} seconds</p>
          <button onClick={startNewGame}>Play Again</button>
        </div>
      )}

      <div
        ref={gridRef}
        className="game-grid"
        style={{
          gridTemplateColumns: `repeat(${puzzle.size}, ${cellSize}px)`,
          touchAction: "none",
        }}
        onPointerMove={handleGridPointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {puzzle.grid.map((row, rowIndex) =>
          row.map((letter, colIndex) => {
            const key = `${rowIndex}-${colIndex}`;
            const isFound = foundCells.has(key);
            const isSelecting = selectionSet.has(key);

            return (
              <div
                key={key}
                className={`game-cell ${isFound ? "game-cell-filled" : ""} ${isSelecting ? "game-cell-selecting" : ""}`}
                style={{
                  width: cellSize,
                  height: cellSize,
                  fontSize: cellSize * 0.5,
                  fontFamily: "monospace",
                  background: isFound ? "var(--cell-found)" : isSelecting ? "var(--cell-active)" : "var(--cell-bg)",
                  color: isFound ? "var(--cell-found-text)" : "var(--fg)",
                }}
                onPointerDown={() => handlePointerDown(rowIndex, colIndex)}
              >
                {letter}
              </div>
            );
          })
        )}
      </div>

      <div className="word-list">
        {puzzle.placedWords.map((pw) => (
          <span key={pw.word} className={`word-item ${foundWords.has(pw.word) ? "found" : ""}`}>
            {pw.word}
          </span>
        ))}
      </div>

      <div className="game-controls">
        <select
          value={theme.name}
          onChange={(e) => {
            const newTheme = THEMES.find((t) => t.name === e.target.value)!;
            setTheme(newTheme);
          }}
          style={{ padding: "12px", fontSize: "1rem" }}
        >
          {THEMES.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>

        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value as Difficulty)}
          style={{ padding: "12px", fontSize: "1rem" }}
        >
          <option value="easy">Easy (8×8)</option>
          <option value="medium">Medium (10×10)</option>
          <option value="hard">Hard (12×12)</option>
        </select>

        <button onClick={startNewGame}>🔄 New Game</button>
      </div>
    </div>
  );
}
